"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = require("react");
const geocodeAddress_1 = require("./geocodeAddress");
const placeSelection_1 = require("./placeSelection");
const DEFAULT_DEBOUNCE_MS = 200;
const useAutocompleteSuggestions = (options = {}) => {
    const { debounceMs = DEFAULT_DEBOUNCE_MS, locationBias = null } = options;
    const [input, setInputState] = (0, react_1.useState)("");
    const [suggestions, setSuggestions] = (0, react_1.useState)([]);
    const [status, setStatus] = (0, react_1.useState)("idle");
    const sessionTokenRef = (0, react_1.useRef)(null);
    const debounceTimerRef = (0, react_1.useRef)(null);
    const requestIdRef = (0, react_1.useRef)(0);
    const locationBiasRef = (0, react_1.useRef)(locationBias);
    const predictionsByIdRef = (0, react_1.useRef)(new Map());
    const suggestionsRef = (0, react_1.useRef)([]);
    (0, react_1.useEffect)(() => {
        locationBiasRef.current = locationBias;
    }, [locationBias]);
    (0, react_1.useEffect)(() => {
        suggestionsRef.current = suggestions;
    }, [suggestions]);
    const ensureSessionToken = (0, react_1.useCallback)(() => {
        var _a, _b;
        if (!sessionTokenRef.current &&
            typeof google !== "undefined" &&
            ((_b = (_a = google.maps) === null || _a === void 0 ? void 0 : _a.places) === null || _b === void 0 ? void 0 : _b.AutocompleteSessionToken)) {
            sessionTokenRef.current =
                new google.maps.places.AutocompleteSessionToken();
        }
        return sessionTokenRef.current;
    }, []);
    const reset = (0, react_1.useCallback)(() => {
        if (debounceTimerRef.current)
            clearTimeout(debounceTimerRef.current);
        sessionTokenRef.current = null;
        setInputState("");
        setSuggestions([]);
        setStatus("idle");
        predictionsByIdRef.current.clear();
    }, []);
    const setInput = (0, react_1.useCallback)((value) => {
        setInputState(value);
    }, []);
    const selectSuggestion = (0, react_1.useCallback)((id) => __awaiter(void 0, void 0, void 0, function* () {
        const suggestion = suggestionsRef.current.find((s) => s.id === id);
        if (!suggestion)
            return null;
        const prediction = predictionsByIdRef.current.get(id);
        try {
            if (prediction) {
                const placeInstance = prediction.toPlace();
                yield placeInstance.fetchFields({
                    fields: [
                        "formattedAddress",
                        "displayName",
                        "location",
                        "viewport",
                        "types",
                        "addressComponents",
                    ],
                });
                const mapped = (0, placeSelection_1.geocodeResultFromFetchedPlace)(placeInstance, suggestion.label);
                if (mapped) {
                    sessionTokenRef.current = null;
                    predictionsByIdRef.current.clear();
                    return mapped;
                }
            }
        }
        catch (err) {
            console.warn("[useAutocompleteSuggestions] Place.fetchFields failed", err);
        }
        const result = yield (0, geocodeAddress_1.geocodeAddress)(suggestion.label);
        sessionTokenRef.current = null;
        predictionsByIdRef.current.clear();
        return result;
    }), []);
    (0, react_1.useEffect)(() => {
        var _a, _b;
        if (debounceTimerRef.current)
            clearTimeout(debounceTimerRef.current);
        const trimmed = input.trim();
        if (!trimmed) {
            setSuggestions([]);
            setStatus("idle");
            predictionsByIdRef.current.clear();
            return;
        }
        if (typeof google === "undefined" ||
            !((_b = (_a = google.maps) === null || _a === void 0 ? void 0 : _a.places) === null || _b === void 0 ? void 0 : _b.AutocompleteSuggestion)) {
            setStatus("error");
            return;
        }
        setStatus("loading");
        debounceTimerRef.current = setTimeout(() => __awaiter(void 0, void 0, void 0, function* () {
            const requestId = ++requestIdRef.current;
            const token = ensureSessionToken();
            const request = {
                input: trimmed,
            };
            if (token)
                request.sessionToken = token;
            if (locationBiasRef.current)
                request.locationBias = locationBiasRef.current;
            try {
                const { suggestions: rawSuggestions } = yield google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
                if (requestId !== requestIdRef.current)
                    return;
                const predictions = rawSuggestions
                    .map((s) => s.placePrediction)
                    .filter((p) => p !== null);
                const nextPredictionsMap = new Map();
                predictions.forEach((p) => nextPredictionsMap.set(p.placeId, p));
                predictionsByIdRef.current = nextPredictionsMap;
                const mapped = predictions.map((p) => {
                    var _a;
                    return ({
                        id: p.placeId,
                        label: p.text.text,
                        matchRanges: ((_a = p.text.matches) !== null && _a !== void 0 ? _a : []).map((m) => ({
                            start: m.startOffset,
                            end: m.endOffset,
                        })),
                    });
                });
                setSuggestions(mapped);
                setStatus(mapped.length === 0 ? "no_results" : "ok");
            }
            catch (_c) {
                if (requestId !== requestIdRef.current)
                    return;
                setSuggestions([]);
                setStatus("error");
                predictionsByIdRef.current.clear();
            }
        }), debounceMs);
        return () => {
            if (debounceTimerRef.current)
                clearTimeout(debounceTimerRef.current);
        };
    }, [input, debounceMs, ensureSessionToken]);
    return {
        input,
        setInput,
        suggestions,
        status,
        reset,
        selectSuggestion,
    };
};
exports.default = useAutocompleteSuggestions;
